import { type PropsWithChildren, memo, useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bell, Eye } from 'lucide-react'
import { NavigationSidebar } from './NavigationSidebar'
import { TopBar } from './TopBar'
import { RightSidebar } from './RightSidebar'
import { MobileMenuToggle } from './MobileMenuToggle'
import { BottomNav } from './BottomNav'
import { useIsDesktop } from '../hooks/useIsDesktop'
import { ROUTES } from '@/app/routes'

type AppShellProps = PropsWithChildren<{
  showAnalytics?: boolean
  showTopBar?: boolean
  onSearch?: (query: string) => void
}>

export const AppShell = memo(function AppShell({
  children,
  showAnalytics = true,
  showTopBar = true,
  onSearch,
}: AppShellProps) {
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const toggleMobileNav = useCallback(() => {
    setMobileNavOpen((prev) => !prev)
  }, [])

  const closeMobileNav = useCallback(() => {
    setMobileNavOpen(false)
  }, [])

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-background">
      {!isDesktop && (
        <header className="sticky top-0 z-20 flex h-11 shrink-0 items-center justify-between border-b border-border bg-background/95 px-3 backdrop-blur-md">
          <div className="flex items-center gap-2">
            <MobileMenuToggle onToggle={toggleMobileNav} isOpen={mobileNavOpen} />
            <Link
              to={ROUTES.app.root}
              className="flex min-h-[44px] items-center gap-1.5 text-foreground no-underline"
            >
              <Eye size={18} strokeWidth={2} />
              <span className="text-sm font-semibold">Omni Sight</span>
            </Link>
          </div>
          <button
            type="button"
            className="relative inline-flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary/50 hover:text-foreground"
            aria-label={t('topbar.notifications')}
          >
            <Bell size={18} strokeWidth={2} />
          </button>
        </header>
      )}

      <div className="flex flex-1 overflow-hidden">
        <NavigationSidebar mobileOpen={mobileNavOpen} onMobileClose={closeMobileNav} />

        <div className="flex flex-1 flex-col overflow-hidden">
          {showTopBar && isDesktop && <TopBar onSearch={onSearch} />}

          <main className="flex-1 overflow-y-auto overflow-x-hidden scroll-smooth px-4 pt-2 pb-20 sm:pt-4 lg:px-0 lg:pb-4">
            {children}
          </main>
        </div>

        {showAnalytics && <RightSidebar />}
      </div>

      {!isDesktop && <BottomNav />}
    </div>
  )
})
